import { type FunctionComponent, useCallback, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { FreeMode } from "swiper/modules";
import "swiper/css";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { notEmpty } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { seriesWidgetQuery$key } from "scmp-app/queries/__generated__/seriesWidgetQuery.graphql";

import { SeriesWidgetTabContent } from "./series-widget-tab-content";
import {
  Container,
  SCMPSeriesLink,
  StyledSwiper,
  StyledSwiperSlide,
  Tab,
  TabsContainer,
} from "./styles";

type Props = {
  reference: seriesWidgetQuery$key;
};

export const HomeSeriesWidget: FunctionComponent<Props> = ({ reference }) => {
  const data = useFragment(
    graphql`
      fragment seriesWidgetQuery on Query
      @argumentDefinitions(seriesWidgetArticlesQueueName: { type: "String!" }) {
        seriesWidgetArticles: queue(filter: { name: $seriesWidgetArticlesQueueName }) {
          items {
            edges {
              node {
                ... on Article {
                  entityId
                  seriesShortLabel
                  series {
                    ... on Content {
                      entityId
                      publishedDate
                    }
                  }
                  ...seriesWidgetTabContentArticle
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const articles =
    data?.seriesWidgetArticles?.items?.edges?.filter(edge => edge.node.seriesShortLabel) ?? [];
  const [tab, setTab] = useState(articles[0]?.node?.entityId);
  const [sentImpressions, setSentImpressions] = useState<Set<string>>(new Set());

  const selectedArticle = articles.find(({ node }) => node.entityId === tab)?.node;
  const widgetTabOrder = articles.findIndex(({ node }) => node.entityId === tab) + 1;
  const label = selectedArticle?.seriesShortLabel?.replaceAll(" ", "_").toLowerCase() ?? "";
  const listOfArticles =
    selectedArticle?.series
      ?.toSorted((a, b) => {
        const dateA = new Date(a?.publishedDate ?? 0).getTime();
        const dateB = new Date(b?.publishedDate ?? 0).getTime();
        return dateB - dateA;
      })
      .slice(0, 3)
      .map(s => s?.entityId)
      .filter(notEmpty)
      .join(",") ?? "";

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(() => {
    setSentImpressions(previous => new Set(previous).add(selectedArticle?.entityId ?? ""));
    return {
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: label,
        content_type: "series", // Required GA4 parameter
        list_of_articles: listOfArticles,
        page_section: "home",
        page_type: "homepage",
        widget_name: "scmp_series",
        widget_tab: widgetTabOrder,
      },
      subcategory: "widget",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: {
        isSendGA4Tracking: true,
        shouldDisable: !selectedArticle || sentImpressions.has(selectedArticle.entityId ?? ""),
        shouldSendOnce: false,
      },
    });

  if (!selectedArticle) {
    return null;
  }

  return (
    <Container ref={captureTrackImpressionEventTargetElement}>
      <SCMPSeriesLink as="/series" pathname="/series">
        SCMP Series
      </SCMPSeriesLink>
      <TabsContainer>
        <StyledSwiper freeMode={true} modules={[FreeMode]} slidesPerView="auto" spaceBetween={0}>
          {articles.map(({ node }) => (
            <StyledSwiperSlide key={node.entityId}>
              <Tab $isActive={tab === node.entityId} onClick={() => setTab(node.entityId)}>
                {node.seriesShortLabel}
              </Tab>
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
      </TabsContainer>
      <SeriesWidgetTabContent order={widgetTabOrder} reference={selectedArticle} />
    </Container>
  );
};

HomeSeriesWidget.displayName = "HomeSeriesWidget";
